import React from 'react';
import { X, UserMinus } from 'lucide-react';
import DialogShell from '../components/ui/DialogShell';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { ClassificationContext } from './transport';
import { changeDealSharing, DealSharing, findSharingCandidates, readDealSharing, sharedDealLink, SharingCandidate } from './sharing';

const validEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
type Change = { recipient: string; revision: string | null; remove: boolean };
export default function ShareDealDialog({ context, rootContractId, onClose }: {
  context: ClassificationContext; rootContractId: string; onClose: () => void;
}) {
  const titleId = React.useId();
  const searchRef = React.useRef<HTMLInputElement>(null);
  const controller = React.useRef(new AbortController());
  const [sharing, setSharing] = React.useState<DealSharing>();
  const [query, setQuery] = React.useState('');
  const lookupController = React.useRef<AbortController>();
  const [finding, setFinding] = React.useState(false);
  const [lookupMessage, setLookupMessage] = React.useState('');
  const [candidates, setCandidates] = React.useState<SharingCandidate[]>([]);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [pending, setPending] = React.useState<Change>();
  const [remove, setRemove] = React.useState<string>();
  const load = React.useCallback(async () => {
    const signal = controller.current.signal;
    try {
      const current = await readDealSharing(context, rootContractId, signal);
      if (!signal.aborted) { setSharing(current); setError(''); }
    } catch (e) { if (!signal.aborted) { setSharing(undefined); setError((e as Error).message); } }
  }, [context, rootContractId]);
  React.useEffect(() => {
    const current = new AbortController(); controller.current = current; void load();
    return () => { current.abort(); lookupController.current?.abort(); };
  }, [load]);
  const findColleague = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!sharing?.can_manage || !validEmail(query) || finding || busy || pending) return;
    lookupController.current?.abort();
    const current = new AbortController(); lookupController.current = current;
    setFinding(true); setError(''); setLookupMessage(''); setCandidates([]);
    try {
      const users = await findSharingCandidates(context, rootContractId, query.trim(), current.signal);
      if (!current.signal.aborted) {
        const available = users.filter(user => !sharing.recipients.some(member => member.user_id === user.user_id));
        setCandidates(available);
        if (!available.length) setLookupMessage(users.length ? 'This colleague already has a deal share.' : 'No colleague is available at that email.');
      }
    } catch (e) { if (!current.signal.aborted) setError((e as Error).message); }
    finally { if (!current.signal.aborted) setFinding(false); }
  };
  const apply = async (change: Change) => {
    if (busy) return;
    setBusy(true); setError(''); setMessage(''); setPending(change);
    try {
      setSharing(await changeDealSharing(context, rootContractId, change.recipient, change.revision, change.remove, controller.current.signal));
      setPending(undefined); setRemove(undefined); setQuery(''); setCandidates([]); setLookupMessage('');
      setMessage(change.remove ? 'Deal share removed.' : 'Deal shared. They can view and edit this deal.');
    } catch (e) {
      if (!controller.current.signal.aborted) {
        setError((e as Error).message);
        if ((e as Error & { confirmedRejected?: boolean }).confirmedRejected) { setPending(undefined); setRemove(undefined); }
      }
    }
    finally { setBusy(false); }
  };
  return <DialogShell open onClose={() => { if (!busy) onClose(); }} labelledBy={titleId}
    backdropPolicy={busy || !!pending ? 'static' : 'dismiss'} initialFocusRef={searchRef}
    overlayClassName="flex items-center justify-center p-4" panelClassName="w-full max-w-md overflow-hidden">
    <header className="flex items-center justify-between p-4 border-b border-border-default">
      <h3 id={titleId} className="text-lg font-semibold text-fg">Share deal</h3>
      <button type="button" disabled={busy} onClick={onClose} aria-label="Close share deal" className="p-1 rounded-full text-fg-tertiary hover:bg-bg-warm"><X size={20} strokeWidth={1.5} aria-hidden="true" /></button>
    </header>
    <div className="p-4 space-y-4">
      <p className="text-sm text-fg-secondary">People in your organization can view and edit this deal. This share covers only this deal; existing project access stays unchanged.</p>
      <p className="text-sm text-fg-secondary">Documents, notes and inputs used in saved calculations are included. Current personal settings and private agent history stay private.</p>
      {!sharing && !error && <p role="status" className="text-sm text-fg-secondary">Loading access…</p>}
      {sharing?.can_manage && <form onSubmit={findColleague}>
        <label className="text-sm font-medium text-fg-secondary" htmlFor={`${titleId}-search`}>Colleague email</label>
        <input id={`${titleId}-search`} ref={searchRef} type="email" value={query} disabled={busy || !!pending}
          onChange={e => { lookupController.current?.abort(); setFinding(false); setQuery(e.target.value); setCandidates([]); setLookupMessage(''); }} placeholder="colleague@example.com"
          className="mt-1 w-full px-3 py-2 border border-border-default rounded-md bg-surface text-fg" />
        <button type="submit" disabled={busy || !!pending || finding || !validEmail(query)} className="mt-2 text-sm text-accent underline">Find colleague</button>
        {finding && <p role="status" className="mt-2 text-sm text-fg-secondary">Finding colleague…</p>}
        {lookupMessage && <p role="status" className="mt-2 text-sm text-fg-secondary">{lookupMessage}</p>}
        {!!candidates.length && <ul className="mt-2 space-y-2">{candidates.map(user => <li key={user.user_id} className="flex justify-between items-center gap-3 p-2 bg-bg-warm rounded-md">
          <span className="text-sm text-fg-secondary">{user.name || user.email}<small className="block text-fg-tertiary">{user.email}</small></span>
          <button type="button" disabled={busy || !!pending} className="text-sm text-accent underline" onClick={() => apply({ recipient: user.user_id, revision: sharing.revision, remove: false })}>Share</button>
        </li>)}</ul>}
      </form>}
      {sharing && <div>
        <h4 className="text-sm font-medium text-fg-secondary mb-2">People with access</h4>
        {!sharing.recipients.length && <p className="text-sm text-fg-tertiary">This deal has not been shared.</p>}
        <ul className="space-y-2">{sharing.recipients.map(member => <li key={member.user_id} className="flex justify-between items-center gap-3 p-3 bg-bg-warm rounded-md">
          <span className="text-sm text-fg-secondary">{member.name || member.email}<small className="block text-fg-tertiary">{member.email}</small></span>
          <span className="text-xs text-fg-tertiary">Can view and edit</span>
          {sharing.can_manage && <button type="button" disabled={busy || !!pending} aria-label={`Remove deal share for ${member.name || member.email}`} className="text-fg-tertiary hover:text-danger" onClick={() => setRemove(member.user_id)}><UserMinus size={18} strokeWidth={1.5} aria-hidden="true" /></button>}
        </li>)}</ul>
      </div>}
      {error && !remove && <p role="alert" className="text-sm text-danger">{error}</p>}
      {!sharing && error && <button type="button" disabled={busy} className="text-sm text-accent underline" onClick={load}>Retry load</button>}
      {pending && !busy && <div className="flex gap-3">
        <button type="button" className="text-sm text-accent underline" onClick={() => apply(pending)}>Retry this change</button>
        <button type="button" className="text-sm text-accent underline" onClick={load}>Check current access</button>
      </div>}
      {message && <p role="status" className="text-sm text-fg-secondary">{message}</p>}
      {sharing?.share_id && <button type="button" className="text-sm text-accent underline" onClick={async () => {
        try { await navigator.clipboard.writeText(window.location.origin + sharedDealLink(context.project_id, rootContractId, sharing.share_id!)); setMessage('Deal link copied. Only people with access can open it.'); }
        catch { setError('The deal link could not be copied.'); }
      }}>Copy deal link</button>}
    </div>
    <ConfirmDialog open={!!remove} onCancel={() => { if (!busy) setRemove(undefined); }}
      title="Remove deal share?" description="This deal share will be removed. Any separate project access stays unchanged."
      error={error}
      confirmLabel="Remove share" cancelLabel="Cancel" variant="destructive" submitting={busy} onConfirm={() => { if (remove && sharing) void apply({ recipient: remove, revision: sharing.revision, remove: true }); }} />
  </DialogShell>;
}
